// report_builder/ReportBuilder.jsx
// ============================================================
// REPORT BUILDER
// Full report definition editor.
// Loads variable/operator registry from API on mount.
// Allows building condition list, naming and saving reports.
//
// Modes:
//   'new'  — blank builder, save creates new report
//   'edit' — loaded from saved report, save updates existing
//
// Props:
//   editReport   — report object to edit (null for new)
//   onSaved      — fn(report) called after save
//   onCancel     — fn() called when user cancels
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import ConditionRow, { defaultCondition } from './ConditionRow';
import { fetchRegistry, createReport, updateReport } from './reportApi';
import ExitBuilder from './ExitBuilder';


// ── Timeframe options for base timeframe selector ────────────
const TF_OPTIONS = ['2min', '5min', '15min', '1H', '1D', '1W'];


// ============================================================
// HELPERS
// ============================================================

let _nextId = 100;
const nextId = () => ++_nextId;

const blankDefinition = () => ({
  schema_version: 1,
  entry: {
    logic:      'flat_and_or',
    conditions: [defaultCondition(nextId())],
  },
  exit: {
    logic:      'flat_and_or',
    conditions: [],
  },
  stop_loss:   { type: null, variable: null, params: null },
  trailing_sl: { type: null, variable: null, params: null },
});


// ============================================================
// REPORT BUILDER
// ============================================================

export default function ReportBuilder({ editReport, onSaved, onCancel }) {
  // ── Registry ─────────────────────────────────────────────
  const [registry,    setRegistry]    = useState(null);
  const [regLoading,  setRegLoading]  = useState(true);
  const [regError,    setRegError]    = useState(null);

  // ── Report meta ───────────────────────────────────────────
  const [name,          setName]          = useState('');
  const [description,   setDescription]   = useState('');
  const [baseTimeframe,   setBaseTimeframe]   = useState('1D');
  const [tradeDirection,  setTradeDirection]  = useState('buy');

  // ── Conditions ────────────────────────────────────────────
  const [conditions,    setConditions]    = useState([defaultCondition(nextId())]);

  // ── Exit + SL ─────────────────────────────────────────────
  const [exitEnabled,   setExitEnabled]   = useState(false);
  const [exitConditions, setExitConditions] = useState([defaultCondition(nextId())]);
  const [slEnabled,     setSlEnabled]     = useState(false);
  const [slConditions,  setSlConditions]  = useState([defaultCondition(nextId())]);

  // ── Risk Management (R3) — initialised straight from editReport ──
  const rdef = editReport?.definition || {};
  const [simpleSlOn,     setSimpleSlOn]     = useState(!!rdef.simple_sl?.enabled);
  const [simpleSlPct,    setSimpleSlPct]    = useState(rdef.simple_sl?.percent ?? 2);
  const [targetOn,       setTargetOn]       = useState(!!rdef.simple_target?.enabled);
  const [targetPct,      setTargetPct]      = useState(rdef.simple_target?.percent ?? 5);
  const [trailType,      setTrailType]      = useState(rdef.trailing_sl?.type || '');
  const [trailPct,       setTrailPct]       = useState(rdef.trailing_sl?.percent ?? 2);
  const [trailEmaPeriod, setTrailEmaPeriod] = useState(rdef.trailing_sl?.ema_period ?? 20);
  const [trailEmaOn,     setTrailEmaOn]     = useState(rdef.trailing_sl?.ema_on || 'close');
  const [trailN,         setTrailN]         = useState(rdef.trailing_sl?.n ?? 5);
  const [maxCandlesOn,   setMaxCandlesOn]   = useState(!!rdef.time_exit?.max_candles);
  const [maxCandles,     setMaxCandles]     = useState(rdef.time_exit?.max_candles ?? 10);
  const [eodExit,        setEodExit]        = useState(!!rdef.time_exit?.eod);

  // ── Save state ────────────────────────────────────────────
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);
  const [saveMsg,    setSaveMsg]    = useState(null);

  const isEdit = !!editReport;

  // ── Load registry on mount ────────────────────────────────
  useEffect(() => {
    setRegLoading(true);
    fetchRegistry()
      .then(data => {
        setRegistry(data);
        setRegLoading(false);
      })
      .catch(err => {
        setRegError('Failed to load variable registry. Check backend.');
        setRegLoading(false);
      });
  }, []);

  // ── Populate fields if editing ────────────────────────────
  useEffect(() => {
    if (!editReport) return;
    setName(editReport.name || '');
    setDescription(editReport.description || '');
    setBaseTimeframe(editReport.base_timeframe || '1D');
    setTradeDirection(editReport.definition?.trade_direction || 'buy');

    const def = editReport.definition || {};

    const conds = def.entry?.conditions || def.conditions || [];
    setConditions(conds.length > 0 ? conds : [defaultCondition(nextId())]);

    const exitConds = def.exit?.conditions || [];
    if (exitConds.length > 0) {
      setExitEnabled(true);
      setExitConditions(exitConds);
    }

    const slConds = def.stop_loss?.conditions || [];
    if (slConds.length > 0) {
      setSlEnabled(true);
      setSlConditions(slConds);
    }
  }, [editReport]);


  // ============================================================
  // CONDITION HANDLERS
  // ============================================================

  const handleConditionChange = useCallback((index, updated) => {
    setConditions(prev => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const handleDelete = useCallback((index) => {
    setConditions(prev => {
      if (prev.length <= 1) return prev;  // keep at least one
      const next = prev.filter((_, i) => i !== index);
      // Ensure first condition has no connector
      if (next.length > 0) next[0] = { ...next[0], connector: null };
      return next;
    });
  }, []);

  const handleConnector = useCallback((index, value) => {
    setConditions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], connector: value };
      return next;
    });
  }, []);

  const handleAddCondition = () => {
    setConditions(prev => [
      ...prev,
      { ...defaultCondition(nextId()), connector: 'AND' },
    ]);
  };

  const handleAddNWrapper = () => {
    setConditions(prev => [
      ...prev,
      {
        id:        nextId(),
        type:      'n_wrapper',
        connector: 'AND',
        mode:      'any',
        n:         5,
        inner: {
          type:     'comparison',
          left:     { variable: 'CLOSE', params: { timeframe: 'auto', offset: 0 } },
          operator: 'lt',
          right:    { variable: 'EMA',   params: { period: 20, on: 'close', timeframe: 'auto', offset: 0 } },
        },
      },
    ]);
  };


  // ============================================================
  // SAVE
  // ============================================================

  const handleSave = async () => {
    setSaveError(null);
    setSaveMsg(null);

    if (!name.trim()) {
      setSaveError('Report name is required.');
      return;
    }
    if (conditions.length === 0) {
      setSaveError('At least one condition is required.');
      return;
    }

    const definition = {
      schema_version:  1,
      trade_direction: tradeDirection,
      entry: {
        logic:      'flat_and_or',
        conditions: conditions.map((c, i) => ({
          ...c,
          id:        c.id || nextId(),
          connector: i === 0 ? null : (c.connector || 'AND'),
        })),
      },
      exit: {
        logic:      'flat_and_or',
        conditions: exitEnabled
          ? exitConditions.map((c, i) => ({
              ...c,
              id:        c.id || nextId(),
              connector: i === 0 ? null : (c.connector || 'AND'),
            }))
          : [],
      },
      stop_loss: {
        logic:      'flat_and_or',
        conditions: slEnabled
          ? slConditions.map((c, i) => ({
              ...c,
              id:        c.id || nextId(),
              connector: i === 0 ? null : (c.connector || 'AND'),
            }))
          : [],
      },
      simple_sl: {
        enabled: simpleSlOn,
        percent: Number(simpleSlPct) || 0,
      },
      simple_target: {
        enabled: targetOn,
        percent: Number(targetPct) || 0,
      },
      trailing_sl: trailType
        ? {
            type:       trailType,
            percent:    Number(trailPct) || 2,
            ema_period: Number(trailEmaPeriod) || 20,
            ema_on:     trailEmaOn,
            n:          Number(trailN) || 5,
          }
        : { type: null },
      time_exit: {
        max_candles: maxCandlesOn ? (Number(maxCandles) || null) : null,
        eod:         eodExit,
      },
    };

    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await updateReport(editReport.id, {
          name:           name.trim(),
          description:    description.trim() || null,
          base_timeframe: baseTimeframe,
          definition,
        });
        setSaveMsg('Report updated successfully.');
      } else {
        saved = await createReport({
          name:           name.trim(),
          description:    description.trim() || null,
          base_timeframe: baseTimeframe,
          definition,
        });
        setSaveMsg('Report saved successfully.');
      }
      if (onSaved) onSaved(saved);
    } catch (err) {
      setSaveError(err?.response?.data?.detail || 'Failed to save report.');
    } finally {
      setSaving(false);
    }
  };


  // ============================================================
  // CONDITION SUMMARY (human readable preview)
  // ============================================================

  const summarizeCondition = (cond) => {
    if (!cond) return '';
    if (cond.type === 'n_wrapper') {
      const inner = cond.inner;
      return `${cond.mode === 'any' ? 'Any' : 'All'}(${cond.n}): ${summarizeInner(inner)}`;
    }
    return summarizeInner(cond);
  };

  const summarizeSide = (side) => {
    if (!side) return '?';
    const { variable, params = {} } = side;
    if (variable === 'EMA')        return `EMA(${params.period},${params.on})`;
    if (variable === 'PREV_N')     return `PrevN(${params.field},${params.n})`;
    if (variable === 'LITERAL')    return String(params.value ?? '?');
    if (variable === 'COLOR_VALUE') return params.value || '?';
    if (variable === 'CANDLE_COLOR') return 'Color';
    return variable || '?';
  };

  const summarizeInner = (cond) => {
    if (!cond) return '';
    const op = registry?.operators[cond.operator]?.label || cond.operator || '';
    return `${summarizeSide(cond.left)} ${op} ${summarizeSide(cond.right)}`;
  };


  // ============================================================
  // RENDER
  // ============================================================

  if (regLoading) {
    return (
      <div style={s.loadingBox}>
        <span style={s.loadingText}>Loading variable registry...</span>
      </div>
    );
  }

  if (regError) {
    return (
      <div style={s.errorBox}>
        <span>{regError}</span>
      </div>
    );
  }

  return (
    <div style={s.container}>

      {/* ── Header ── */}
      <div style={s.header}>
        <span style={s.headerTitle}>
          {isEdit ? `Edit Report — ${editReport.name}` : 'New Report'}
        </span>
        <div style={s.headerActions}>
          <button style={s.btnSecondary} onClick={onCancel}>
            Cancel
          </button>
          <button
            style={{ ...s.btnPrimary, ...(saving ? s.btnDisabled : {}) }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Report' : 'Save Report'}
          </button>
        </div>
      </div>

      {/* ── Save messages ── */}
      {saveError && (
        <div style={s.msgError}>
          {saveError}
          <button style={s.msgClose} onClick={() => setSaveError(null)}>✕</button>
        </div>
      )}
      {saveMsg && (
        <div style={s.msgSuccess}>
          {saveMsg}
          <button style={s.msgClose} onClick={() => setSaveMsg(null)}>✕</button>
        </div>
      )}

      <div style={s.body}>

        {/* ── Left panel — meta + conditions ── */}
        <div style={s.leftPanel}>

          {/* Report meta */}
          <div style={s.section}>
            <div style={s.sectionTitle}>Report Details</div>
            <div style={s.metaRow}>
              <div style={s.fieldGroup}>
                <label style={s.label}>Name *</label>
                <input
                  style={s.textInput}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. EMA Pullback Reclaim"
                  maxLength={100}
                />
              </div>
              <div style={s.fieldGroup}>
                <label style={s.label}>Default Timeframe</label>
                <select
                  style={s.select}
                  value={baseTimeframe}
                  onChange={e => setBaseTimeframe(e.target.value)}
                >
                  {TF_OPTIONS.map(tf => (
                    <option key={tf} value={tf}>{tf}</option>
                  ))}
                </select>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>Trade Direction</label>
                <select
                  style={s.select}
                  value={tradeDirection}
                  onChange={e => setTradeDirection(e.target.value)}
                >
                  <option value="buy">📈 Buy (Long)</option>
                  <option value="sell">📉 Sell (Short)</option>
                </select>
              </div>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Description</label>
              <input
                style={{ ...s.textInput, width: '100%' }}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Optional — describe what this report finds"
                maxLength={300}
              />
            </div>
          </div>

          {/* Entry conditions */}
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <span style={s.sectionTitle}>Entry Conditions</span>
              <div style={s.addBtns}>
                <button style={s.btnAdd} onClick={handleAddCondition}>
                  + Condition
                </button>
                <button style={s.btnAddWrapper} onClick={handleAddNWrapper}>
                  + Any / All
                </button>
              </div>
            </div>

            {/* Condition rows */}
            <div style={s.conditionList}>
              {conditions.map((cond, idx) => (
                <ConditionRow
                  key={cond.id ?? idx}
                  condition={cond}
                  index={idx}
                  registry={registry}
                  onChange={handleConditionChange}
                  onDelete={handleDelete}
                  onConnector={handleConnector}
                  isFirst={idx === 0}
                />
              ))}
            </div>
          </div>

          {/* Exit Conditions */}
          <ExitBuilder
            title="Exit Conditions"
            subtitle="When to close the trade"
            conditions={exitConditions}
            onChange={setExitConditions}
            registry={registry}
            enabled={exitEnabled}
            onToggle={setExitEnabled}
          />

          {/* Stop Loss Conditions */}
          <ExitBuilder
            title="Stop Loss"
            subtitle="Force exit to limit losses"
            conditions={slConditions}
            onChange={setSlConditions}
            registry={registry}
            enabled={slEnabled}
            onToggle={setSlEnabled}
          />

          {/* ── Risk Management (R3) ── */}
          <div style={s.riskSection}>
            <div style={s.riskTitle}>Risk Management</div>

            {/* Simple SL % */}
            <div style={s.riskRow}>
              <label style={s.riskLabel}>
                <input
                  type="checkbox"
                  checked={simpleSlOn}
                  onChange={e => setSimpleSlOn(e.target.checked)}
                />
                {' '}Stop Loss %
              </label>
              {simpleSlOn && (
                <>
                  <input
                    type="number" style={s.riskInput}
                    min="0.1" step="0.1"
                    value={simpleSlPct}
                    onChange={e => setSimpleSlPct(e.target.value)}
                  />
                  <span style={s.riskHint}>% from entry price → SL_HIT</span>
                </>
              )}
            </div>

            {/* Target % */}
            <div style={s.riskRow}>
              <label style={s.riskLabel}>
                <input
                  type="checkbox"
                  checked={targetOn}
                  onChange={e => setTargetOn(e.target.checked)}
                />
                {' '}Target %
              </label>
              {targetOn && (
                <>
                  <input
                    type="number" style={s.riskInput}
                    min="0.1" step="0.1"
                    value={targetPct}
                    onChange={e => setTargetPct(e.target.value)}
                  />
                  <span style={s.riskHint}>% from entry price → TARGET_HIT</span>
                </>
              )}
            </div>

            {/* Trailing SL */}
            <div style={s.riskRow}>
              <span style={s.riskLabel}>Trailing SL</span>
              <select
                style={s.riskSelect}
                value={trailType}
                onChange={e => setTrailType(e.target.value)}
              >
                <option value="">None</option>
                <option value="percent">% from best price</option>
                <option value="ema">Close vs EMA</option>
                <option value="n_low">N-candle low/high</option>
              </select>

              {trailType === 'percent' && (
                <>
                  <input
                    type="number" style={s.riskInput}
                    min="0.1" step="0.1"
                    value={trailPct}
                    onChange={e => setTrailPct(e.target.value)}
                  />
                  <span style={s.riskHint}>
                    exit when close falls this % from best close since entry
                  </span>
                </>
              )}

              {trailType === 'ema' && (
                <>
                  <select
                    style={s.riskSelect}
                    value={trailEmaPeriod}
                    onChange={e => setTrailEmaPeriod(Number(e.target.value))}
                  >
                    {[5, 9, 20, 50, 200].map(p => (
                      <option key={p} value={p}>EMA {p}</option>
                    ))}
                  </select>
                  <select
                    style={s.riskSelect}
                    value={trailEmaOn}
                    onChange={e => setTrailEmaOn(e.target.value)}
                  >
                    {['close', 'high', 'low'].map(o => (
                      <option key={o} value={o}>on {o}</option>
                    ))}
                  </select>
                  <span style={s.riskHint}>exit when close crosses the EMA against the trade</span>
                </>
              )}

              {trailType === 'n_low' && (
                <>
                  <input
                    type="number" style={s.riskInput}
                    min="1" step="1"
                    value={trailN}
                    onChange={e => setTrailN(e.target.value)}
                  />
                  <span style={s.riskHint}>
                    buy: exit below lowest low of last N · sell: above highest high
                  </span>
                </>
              )}
            </div>

            {/* Time exits */}
            <div style={s.riskRow}>
              <label style={s.riskLabel}>
                <input
                  type="checkbox"
                  checked={maxCandlesOn}
                  onChange={e => setMaxCandlesOn(e.target.checked)}
                />
                {' '}Max holding
              </label>
              {maxCandlesOn && (
                <>
                  <input
                    type="number" style={s.riskInput}
                    min="1" step="1"
                    value={maxCandles}
                    onChange={e => setMaxCandles(e.target.value)}
                  />
                  <span style={s.riskHint}>candles → TIME_EXIT</span>
                </>
              )}
            </div>

            <div style={s.riskRow}>
              <label style={s.riskLabel}>
                <input
                  type="checkbox"
                  checked={eodExit}
                  onChange={e => setEodExit(e.target.checked)}
                />
                {' '}End-of-day square-off
              </label>
              <span style={s.riskHint}>
                intraday timeframes only — exits on the day's last candle,
                skips entries on that candle
              </span>
            </div>
          </div>

        </div>

        {/* ── Right panel — live summary ── */}
        <div style={s.rightPanel}>
          <div style={s.sectionTitle}>Condition Summary</div>
          <div style={s.summaryBox}>
            {conditions.length === 0 && (
              <div style={s.summaryEmpty}>No conditions yet.</div>
            )}
            {conditions.map((cond, idx) => (
              <div key={cond.id ?? idx} style={s.summaryRow}>
                {idx > 0 && (
                  <span style={{
                    ...s.summaryConnector,
                    color: cond.connector === 'OR' ? '#e65100' : '#2962ff',
                  }}>
                    {cond.connector || 'AND'}
                  </span>
                )}
                <span style={s.summaryText}>
                  {idx + 1}. {summarizeCondition(cond)}
                </span>
              </div>
            ))}
          </div>

          {/* Registry quick reference */}
          <div style={{ ...s.sectionTitle, marginTop: '20px' }}>
            Variable Reference
          </div>
          <div style={s.refBox}>
            {Object.entries(
              Object.entries(registry.variables).reduce((acc, [key, entry]) => {
                if (!acc[entry.category]) acc[entry.category] = [];
                acc[entry.category].push({ key, label: entry.label });
                return acc;
              }, {})
            ).map(([cat, items]) => (
              <div key={cat} style={s.refCategory}>
                <div style={s.refCatLabel}>{cat}</div>
                {items.map(({ key, label }) => (
                  <div key={key} style={s.refItem}>
                    <span style={s.refKey}>{label}</span>
                    <span style={s.refParams}>
                      {registry.variables[key].params
                        .map(p => p.label)
                        .join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    overflow:      'hidden',
    background:    '#f7f8fa',
  },
  header: {
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    padding:         '10px 16px',
    background:      '#fff',
    borderBottom:    '1px solid #e0e0e0',
    flexShrink:      0,
  },
  headerTitle: {
    fontSize:   '14px',
    fontWeight: 'bold',
    color:      '#131722',
  },
  headerActions: {
    display: 'flex',
    gap:     '8px',
  },
  body: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
    gap:      '0px',
  },
  leftPanel: {
    flex:      1,
    overflowY: 'auto',
    padding:   '16px',
    display:   'flex',
    flexDirection: 'column',
    gap:       '12px',
  },
  rightPanel: {
    width:        '260px',
    flexShrink:   0,
    overflowY:    'auto',
    padding:      '16px',
    borderLeft:   '1px solid #e0e0e0',
    background:   '#fff',
  },
  section: {
    background:   '#fff',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    padding:      '12px 14px',
  },
  sectionHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   '10px',
  },
  sectionTitle: {
    fontSize:     '12px',
    fontWeight:   'bold',
    color:        '#444',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  comingSoon: {
    fontSize:   '11px',
    color:      '#aaa',
    fontWeight: 'normal',
    textTransform: 'none',
    marginLeft: '4px',
  },
  metaRow: {
    display: 'flex',
    gap:     '12px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  },
  fieldGroup: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  label: {
    fontSize: '11px',
    color:    '#666',
  },
  textInput: {
    padding:      '5px 8px',
    fontSize:     '13px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    color:        '#333',
    width:        '240px',
    outline:      'none',
  },
  select: {
    padding:      '5px 8px',
    fontSize:     '13px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    color:        '#333',
    background:   '#fff',
    cursor:       'pointer',
  },
  conditionList: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  addBtns: {
    display: 'flex',
    gap:     '6px',
  },
  btnAdd: {
    padding:         '4px 10px',
    fontSize:        '12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  btnAddWrapper: {
    padding:         '4px 10px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#555',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  btnPrimary: {
    padding:         '6px 16px',
    fontSize:        '13px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  btnSecondary: {
    padding:         '6px 14px',
    fontSize:        '13px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  btnDisabled: {
    backgroundColor: '#bbb',
    cursor:          'not-allowed',
  },
  msgError: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '8px 14px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    borderBottom:    '1px solid #ffcdd2',
    fontSize:        '13px',
    flexShrink:      0,
  },
  msgSuccess: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '8px 14px',
    backgroundColor: '#f0fff4',
    color:           '#2e7d32',
    borderBottom:    '1px solid #c8e6c9',
    fontSize:        '13px',
    flexShrink:      0,
  },
  msgClose: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '14px',
    color:      'inherit',
  },
  placeholder: {
    fontSize: '12px',
    color:    '#aaa',
    padding:  '8px 0',
  },
  // Summary panel
  summaryBox: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
    padding:       '8px',
    background:    '#f7f8fa',
    borderRadius:  '4px',
    border:        '1px solid #e0e0e0',
    minHeight:     '60px',
  },
  summaryEmpty: {
    fontSize: '12px',
    color:    '#bbb',
  },
  summaryRow: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '2px',
  },
  summaryConnector: {
    fontSize:   '10px',
    fontWeight: 'bold',
  },
  summaryText: {
    fontSize:  '12px',
    color:     '#333',
    wordBreak: 'break-word',
  },
  // Reference panel
  refBox: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '10px',
  },
  refCategory: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '3px',
  },
  refCatLabel: {
    fontSize:      '10px',
    fontWeight:    'bold',
    color:         '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom:  '2px',
  },
  refItem: {
    display:  'flex',
    gap:      '6px',
    fontSize: '11px',
  },
  refKey: {
    color:      '#2962ff',
    fontWeight: 'bold',
    minWidth:   '60px',
  },
  refParams: {
    color: '#888',
  },
  loadingBox: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100%',
    color:          '#888',
    fontSize:       '13px',
  },
  loadingText: { color: '#888' },
  errorBox: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100%',
    color:          '#d32f2f',
    fontSize:       '13px',
  },

  // ── R3 Risk Management panel ──
  riskSection: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '8px',
    padding:       '10px 12px',
    background:    '#fff',
    border:        '1px solid #e0e0e0',
    borderRadius:  '6px',
  },
  riskTitle: {
    fontSize:      '12px',
    fontWeight:    'bold',
    color:         '#131722',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  riskRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    flexWrap:   'wrap',
  },
  riskLabel: {
    fontSize:   '12px',
    color:      '#333',
    minWidth:   '120px',
    fontWeight: 'bold',
    cursor:     'pointer',
  },
  riskInput: {
    width:        '70px',
    padding:      '3px 6px',
    fontSize:     '12px',
    border:       '1px solid #ccc',
    borderRadius: '4px',
  },
  riskSelect: {
    padding:      '3px 6px',
    fontSize:     '12px',
    border:       '1px solid #ccc',
    borderRadius: '4px',
  },
  riskHint: {
    fontSize: '11px',
    color:    '#999',
  },
};